/* eslint-disable react-hooks/exhaustive-deps */
import "./retireeNewsletter.scss";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import FileInput from "../../fileInput/fileInput";
import { Formik } from "formik";
import * as Yup from "yup";
import * as retireeNewsletterAction from "../../../redux/actions/retireeNewsletter.action";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import { useTranslation } from "react-i18next";
import * as React from "react";
import ModalConfirmation from "../../modalconfirmation/ModalConfirmation";

const RetireeNewsletter = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [renderSuccessAlert, setRenderSuccessAlert] = useState(false);
  const [submittedForm, setSubmittedForm] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const MAX_FILE_SIZE_IMAGE = 2 * 1024 * 1024; //2MB
  const MAX_FILE_SIZE_PDF = 10 * 1024 * 1024; //10MB
  const VALID_FILE_TYPES_IMAGE = ["image/png", "image/jpeg"];
  const VALID_FILE_TYPES_PDF = ["application/pdf"];
  const [showExitModal, setShowExitModal] = useState(false);

  const {
    retireeNewsletterDetails,
    retireeNewsletter,
    error,
  } = useSelector((state) => ({
    retireeNewsletterDetails: state.retireeNewsletter.retireeNewsletterDetails,
    retireeNewsletter: state.retireeNewsletter.retireeNewsletter,
    error: state.retireeNewsletter.retireeNewsletterError,
  }));

  // Flatten the file structure for FileInput component
  const getInitialFileValue = (fileData) => {
    if (!fileData?.data?.attributes) return "";

    const { name, url, mime } = fileData.data.attributes;
    return {
      name,
      url,
      type: mime,
      mime,
      // Keep the original structure for validation
      data: fileData.data
    };
  };

  const initialValues = {
    image: retireeNewsletterDetails?.data?.attributes?.image ? getInitialFileValue(retireeNewsletterDetails.data.attributes.image) : "",
    pdfFile: retireeNewsletterDetails?.data?.attributes?.pdf ? getInitialFileValue(retireeNewsletterDetails.data.attributes.pdf) : "",
    title: retireeNewsletterDetails?.data?.attributes?.title || "",
    titleForLeftMenu: retireeNewsletterDetails?.data?.attributes?.titleForLeftMenu || "",
  };

  const formSchema = Yup.object().shape({
    title: Yup.string().required("Required field"),
    titleForLeftMenu: Yup.string()
      .required("Required field")
      .max(50, "Short label must be 50 characters or less"),
    image: Yup.mixed()
      .required("Required field")
      .test(
        "is-correct-size",
        "File too large",
        (value, _) => {
          // If it's an existing file (object from Strapi), skip size validation
          if (value && typeof value === 'object' && !value.size) {
            return true;
          }
          return (value?.size || 0) <= MAX_FILE_SIZE_IMAGE;
        }
      )
      .test(
        "is-correct-format",
        "Invalid file extension. Only PNG and JPG files are allowed.",
        (value, _) => {
          // If no value, fail validation
          if (!value) return false;

          // If it's an existing file (object from Strapi), check the nested structure
          if (typeof value === 'object' && value.data?.attributes?.mime) {
            return VALID_FILE_TYPES_IMAGE.includes(value.data.attributes.mime);
          }

          // If it's a new file upload, check type or mime
          if (value.type || value.mime) {
            return VALID_FILE_TYPES_IMAGE.includes(value.type || value.mime);
          }

          return false;
        }
      ),
    pdfFile: Yup.mixed()
      .required("Required field")
      .test(
        "is-correct-size",
        "File too large",
        (value, _) => {
          // If it's an existing file (object from Strapi), skip size validation
          if (value && typeof value === 'object' && !value.size) {
            return true;
          }
          return (value?.size || 0) <= MAX_FILE_SIZE_PDF;
        }
      )
      .test(
        "is-correct-format",
        "Invalid file extension. Only PDF files are allowed.",
        (value, _) => {
          // If no value, fail validation
          if (!value) return false;

          // If it's an existing file (object from Strapi), check the nested structure
          if (typeof value === 'object' && value.data?.attributes?.mime) {
            return VALID_FILE_TYPES_PDF.includes(value.data.attributes.mime);
          }

          // If it's a new file upload, check type or mime
          if (value.type || value.mime) {
            return VALID_FILE_TYPES_PDF.includes(value.type || value.mime);
          }

          return false;
        }
      ),
  });

  const onSubmitForm = async (values) => {
    setDisableSubmit(true); //Disable submit button

    const formData = new FormData();

    const { title, titleForLeftMenu, image, pdfFile } = values;

    const formBody = {
      title,
      titleForLeftMenu,
      current_newsletter: true
    };

    formData.append("data", JSON.stringify(formBody));

    // Only append files if they are new uploads (have a size property)
    if (image && image.size) {
      formData.append("files.image", image);
    }
    if (pdfFile && pdfFile.size) {
      formData.append("files.pdf", pdfFile);
    }

		await retireeNewsletterAction.update(dispatch, retireeNewsletterDetails?.data?.id, formData);

    setSubmittedForm(true); // Flag to make sure that the form was submitted
    setDisableSubmit(false); //Reenable submit button
  };

  useEffect(() => {
    retireeNewsletterAction.fetchDetails(dispatch);
  }, [dispatch]);

  useEffect(() => {
    //Render success alert and redirect
    if (submittedForm && retireeNewsletter) {
      setRenderSuccessAlert(true);
    }
  }, [retireeNewsletter, submittedForm, navigate]);


  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={formSchema}
      onSubmit={onSubmitForm}
    >
      {(formik) => {
        const {
          handleChange,
          handleSubmit,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          values,
        } = formik;

        return (
          <div className="retiree-newsletter">
            <div className="retiree-newsletter-container">
              {error && <Alert variant="danger">{error}</Alert>}
              {renderSuccessAlert && (
                <Alert
                  variant="success"
                  onClose={() => {
                    setRenderSuccessAlert(false);
                  }}
                >
                  {t("newsletters.editSuccess")}
                </Alert>
              )}
              <div className="retiree-newsletter-header">
                <h1 className="retiree-newsletter-title">
                  Current Medicare Newsletter
                </h1>
              </div>
              <Form className="retiree-newsletter-form">
                <div className="retiree-newsletter-form-layout">
                  <div className="form-row">
                    <div className="form-input">
                      <Form.Label>{t("newsletters.title")}</Form.Label>
                      <Form.Control
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={errors.title && touched.title}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.title}
                      </Form.Control.Feedback>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-input">
                      <Form.Label>Short Label for Left Menu</Form.Label>
                      <Form.Control
                        name="titleForLeftMenu"
                        value={values.titleForLeftMenu}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={errors.titleForLeftMenu && touched.titleForLeftMenu}
                        placeholder="Max 50 characters"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.titleForLeftMenu}
                      </Form.Control.Feedback>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-input">
                      <Form.Label>{t("newsletters.image")}</Form.Label>
                      <FileInput
                        title="Browse an Image"
                        allowedTypes={"PNG, JPG"}
                        value={values.image}
                        customFieldName={"image"}
                        setFieldValue={setFieldValue}
                        errorMessage={errors.image}
                      />
                      <div
                        style={{
                          width: "100%",
                          marginTop: "0.25rem",
                          fontSize: ".875em",
                          color: "#dc3545",
                          display: "block",
                        }}
                      >
                        {errors.image}
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-input">
                      <Form.Label>PDF File</Form.Label>
                      <FileInput
                        title="Browse a PDF File"
                        allowedTypes={"PDF"}
                        value={values.pdfFile}
                        customFieldName={"pdfFile"}
                        setFieldValue={setFieldValue}
                        errorMessage={errors.pdfFile}
                      />
                      <div
                        style={{
                          width: "100%",
                          marginTop: "0.25rem",
                          fontSize: ".875em",
                          color: "#dc3545",
                          display: "block",
                        }}
                      >
                        {errors.pdfFile}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-action-group">
                  <Button
                    type="submit"
                    className="form-action form-action-primary"
                    variant="outline-primary"
                    onClick={(e) => {
                      handleSubmit(e);
                    }}
                    disabled={disableSubmit}
                  >
                    {t("buttons.save")}
                  </Button>
                </div>
              </Form>
            </div>
            <ModalConfirmation
              show={showExitModal}
              handleShow={setShowExitModal}
              title={t("modalconfirmation.titleExit")}
              mainAction={() => setShowExitModal(false)}
              secondaryAction={() => window.history.back()}
              question={t("modalconfirmation.exitquestion")}
              mainButton={t("modalconfirmation.continue")}
              secondaryButton={t("modalconfirmation.yesleave")}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default RetireeNewsletter;